import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { graphql } from 'gql';
import type { ProjectData } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isAuthenticatedSelector } from '../app/selectors';

const gqlProject = graphql(`
    query project($uniqueId: String!) {
        project(uniqueId: $uniqueId) {
            result
            message
            hasValidation
            validation {
                property
                message
            }
            data {
                code
                uniqueId
                statusFollowers
                stageFollowers
                name
                stage
                status
                methodology
                contractType
                contractId
                contractUniqueId
                calculationNavigation {
                    result
                    message
                    hasValidation
                    validation {
                        metadata
                        property
                        message
                    }
                    data {
                        calculationNavData {
                            name
                            hasCalculation
                            hasCalculationObjects
                            hasWOR
                            navLinkId
                            metadata
                            id
                        }
                        calculationNavItems {
                            navLinkId
                            name
                            metadata
                            id
                        }
                        metadata
                    }
                }
                contractNavigation {
                    result
                    message
                    hasValidation
                    validation {
                        metadata
                        property
                        message
                    }
                    data {
                        contractNavData {
                            id
                            hasContract
                            hasWorkOrders
                            status
                            internalNumber
                            authoredBy
                            createdAt
                            navLinkId
                            name
                            metadata
                        }
                        contractNavItems {
                            navLinkId
                            name
                            metadata
                            id
                        }
                        workOrderNavs {
                            id
                            name
                            status
                            internalNumber
                            navLinkId
                            signingDate
                            actKC2Navs {
                                id
                                date
                                number
                                status
                                season {
                                    id
                                    year
                                }
                                costGroup
                                correctionNumber
                                correctionDate
                                fullNumber
                                isCorrection
                                isInactive
                                correctionDate
                            }
                            actKC3Navs {
                                id
                                number
                                date
                                actsKC2 {
                                    id
                                    date
                                    number
                                    status
                                    season {
                                        id
                                        year
                                    }
                                    costGroup
                                    correctionNumber
                                    correctionDate
                                    fullNumber
                                    isCorrection
                                    isInactive
                                    correctionDate
                                }
                                correctionNumber
                                fullNumber
                                isCorrection
                                isInactive
                            }
                            workOrderChange {
                                id
                                name
                                signingDate
                                workOrderNumber
                                workOrderChangeNumber
                                status
                                navLinkId
                            }
                            workOrderChanges {
                                id
                                signingDate
                                workOrderNumber
                                workOrderChangeNumber
                                status
                                navLinkId
                                name
                            }
                            metadata
                        }
                    }
                }
                subsidiary {
                    id
                    name
                }
                contractPrice {
                    hasValidation
                    validation {
                        message
                        metadata
                        property
                    }
                    result
                    message
                    data {
                        contractPlan
                        actualCost
                        hasActualCost
                    }
                }
                calculationPrice {
                    hasValidation
                    validation {
                        message
                        metadata
                        property
                    }
                    message
                    result
                    data {
                        priceNMC
                    }
                }
                createdAt
                updatedAt
                authoredBy
                calculationId
                calculationStatus
                calculationUniqueId
                closedAt
                contractInternalNumber
                contractStatus
                stageFollowers
            }
        }
    }
`);

export const useProjectData = (id?: string, withEffect = true) => {
    const variables = useMemo(() => ({ uniqueId: id ?? '' }), [id]);
    const isAuthenticated = useSelector(isAuthenticatedSelector);
    const dispatch = useDispatch();

    const [projectData, projectDataStatus, projectDataError, makeRequest, setData] = useRequest({
        requestId: `getProject_${id}`,
        query: gqlProject,
        variables,
        withEffect: false,
    });

    const validationProjectData = useMemo(
        () => ({
            hasValidation: projectData?.project?.hasValidation,
            message: projectData?.project?.message,
            result: projectData?.project?.result,
            validation: projectData?.project?.validation,
        }),
        [projectData?.project],
    );

    const refreshProject = useCallback(() => {
        dispatch(makeRequest(variables));
    }, [dispatch, variables, makeRequest]);

    const refreshProjectWithSuccessCB = useCallback(
        (afterUpdateCB?: () => void) => {
            dispatch(makeRequest(variables, afterUpdateCB));
        },
        [dispatch, variables, makeRequest],
    );

    const setProjectData = useCallback(
        (data: ProjectData) => {
            dispatch(setData({ project: { data } }));
        },
        [dispatch, setData],
    );

    useEffect(() => {
        if (
            withEffect &&
            isAuthenticated &&
            !!id &&
            !projectData &&
            projectDataStatus !== RequestStatus.FETCHING &&
            projectDataStatus !== RequestStatus.ERROR
        ) {
            refreshProject();
        }
    }, [projectData, id, isAuthenticated, refreshProject, projectDataStatus, withEffect]);

    const isProjectLoading = projectDataStatus === RequestStatus.FETCHING;

    return useMemo(
        () => ({
            projectData: projectData?.project?.data,
            projectDataStatus,
            isProjectLoading,
            projectDataError,
            refreshProject,
            refreshProjectWithSuccessCB,
            validationProjectData,
            setProjectData,
        }),
        [
            projectData?.project?.data,
            projectDataStatus,
            isProjectLoading,
            projectDataError,
            refreshProject,
            refreshProjectWithSuccessCB,
            validationProjectData,
            setProjectData,
        ],
    );
};
